import React, { useMemo, useState } from 'react';
import { Card, IndexTable, Badge, Button, InlineStack, TextField, Modal, Text, EmptySearchResult, ButtonGroup } from '@shopify/polaris';
import { useStore } from '../StoreContext';

import { hasRole } from '../constants/roles';
const CODE_HELP = {
  '81277419698': 'Tinton Falls shelf code: one or two letters, dash, two digits (A-01, AB-12).',
  '71312965810': 'Teams Lancaster shelf code: 4 numbers joined by dashes (1-2-3-4).',
};

export default function LocationsTab({ api, warehouse, locations, reloadLocations, toast, user }) {
  const { activeStore } = useStore();
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);           // location row
  const [form, setForm] = useState({ code: '', zone: '', shelf_barcode: '' });
  const [busy, setBusy] = useState('');

  const rows = useMemo(() => (locations || [])
    .filter(l => showInactive || l.active)
    .filter(l => !filter || l.code.toLowerCase().includes(filter.toLowerCase()) || (l.zone || '').toLowerCase().includes(filter.toLowerCase())),
    [locations, showInactive, filter]);

  const run = async (label, fn, okMsg) => {
    setBusy(label);
    try { const j = await fn(); toast('ok', okMsg ? okMsg(j) : `${label} done`); await reloadLocations(); return true; }
    catch (e) { toast('err', e.message); return false; }
    finally { setBusy(''); }
  };

  const openLabel = (l) => {
    const qs = new URLSearchParams({ store: activeStore, shopify_location_id: warehouse.shopify_location_id });
    window.open(`/api/rfid/locations/${l.id}/label.pdf?${qs}`, '_blank', 'noopener,noreferrer');
  };

  const submitAdd = async () => {
    const ok = await run('Add', () => api('/api/rfid/locations', { method: 'POST', body: {
      code: form.code.trim().toUpperCase(), zone: form.zone.trim() || null, shelf_barcode: form.shelf_barcode.trim() || null,
    } }), () => `Added ${form.code.trim().toUpperCase()}`);
    if (ok) { setAdding(false); setForm({ code: '', zone: '', shelf_barcode: '' }); }
  };

  const submitEdit = async () => {
    const ok = await run('Save', () => api(`/api/rfid/locations/${editing.id}`, { method: 'PUT', body: {
      zone: form.zone.trim(), shelf_barcode: form.shelf_barcode.trim(),
    } }), () => `Saved ${editing.code}`);
    if (ok) setEditing(null);
  };

  const toggleActive = (l) => run(l.active ? 'Deactivate' : 'Reactivate',
    () => api(`/api/rfid/locations/${l.id}`, { method: 'PUT', body: { active: !l.active } }),
    () => `${l.code} ${l.active ? 'deactivated' : 'reactivated'}`);

  const seed = (mode) => run(`Seed ${mode}`,
    () => api('/api/rfid/locations/seed', { method: 'POST', body: { mode } }),
    (j) => `Seed from ${mode === 'bins' ? 'bin metafield' : 'overstock metafield'}: ${j.created} created, ${j.skipped} skipped`);

  const isAdmin = hasRole(user, ['admin']);

  return (
    <Card>
      <InlineStack gap="300" blockAlign="end" wrap>
        <TextField label="Filter" value={filter} onChange={setFilter} autoComplete="off" clearButton onClearButtonClick={() => setFilter('')} placeholder="code or zone" />
        <Button pressed={showInactive} onClick={() => setShowInactive(v => !v)}>{showInactive ? 'Hide inactive' : 'Show inactive'}</Button>
        <Button variant="primary" onClick={() => { setForm({ code: '', zone: '', shelf_barcode: '' }); setAdding(true); }}>Add location</Button>
        {isAdmin && (
          <ButtonGroup>
            <Button loading={busy === 'Seed overstock'} onClick={() => seed('overstock')}>Seed from overstock metafield</Button>
            <Button loading={busy === 'Seed bins'} onClick={() => seed('bins')}>Seed from bin metafield</Button>
          </ButtonGroup>
        )}
        <Text as="span" tone="subdued">{rows.length} of {(locations || []).length} locations</Text>
      </InlineStack>
      <div style={{ height: 12 }} />
      <IndexTable
        resourceName={{ singular: 'location', plural: 'locations' }}
        itemCount={rows.length}
        selectable={false}
        emptyState={<EmptySearchResult title="No locations" description="Add one or seed from the metafield." withIllustration />}
        headings={[{ title: 'Code' }, { title: 'Zone' }, { title: 'Shelved boxes' }, { title: 'Status' }, { title: 'Actions' }]}
      >
        {rows.map((l, i) => (
          <IndexTable.Row id={String(l.id)} key={l.id} position={i}>
            <IndexTable.Cell><span className="overstock-mono">{l.code}</span></IndexTable.Cell>
            <IndexTable.Cell>{l.zone || '—'}</IndexTable.Cell>
            <IndexTable.Cell>{l.box_count}</IndexTable.Cell>
            <IndexTable.Cell><Badge tone={l.active ? 'success' : undefined}>{l.active ? 'active' : 'inactive'}</Badge></IndexTable.Cell>
            <IndexTable.Cell>
              <ButtonGroup>
                <Button size="slim" onClick={() => { setForm({ code: l.code, zone: l.zone || '', shelf_barcode: l.shelf_barcode || '' }); setEditing(l); }}>Edit</Button>
                <Button size="slim" onClick={() => openLabel(l)}>Shelf label</Button>
                <Button size="slim" tone={l.active ? 'critical' : undefined} disabled={l.active && l.box_count > 0}
                        onClick={() => toggleActive(l)}>{l.active ? 'Deactivate' : 'Reactivate'}</Button>
              </ButtonGroup>
            </IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add location"
             primaryAction={{ content: 'Add', onAction: submitAdd, loading: busy === 'Add', disabled: !form.code.trim() }}>
        <Modal.Section>
          <TextField label="Code" value={form.code} onChange={v => setForm(f => ({ ...f, code: v }))} autoComplete="off" helpText={CODE_HELP[String(warehouse.shopify_location_id)]} />
          <TextField label="Zone (optional)" value={form.zone} onChange={v => setForm(f => ({ ...f, zone: v }))} autoComplete="off" />
          <TextField label="Shelf barcode (optional)" value={form.shelf_barcode} onChange={v => setForm(f => ({ ...f, shelf_barcode: v }))} autoComplete="off" helpText="Defaults to OS:<code>" />
        </Modal.Section>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Edit ${editing.code}` : ''}
             primaryAction={{ content: 'Save', onAction: submitEdit, loading: busy === 'Save' }}>
        <Modal.Section>
          <TextField label="Zone" value={form.zone} onChange={v => setForm(f => ({ ...f, zone: v }))} autoComplete="off" />
          <TextField label="Shelf barcode" value={form.shelf_barcode} onChange={v => setForm(f => ({ ...f, shelf_barcode: v }))} autoComplete="off" />
        </Modal.Section>
      </Modal>
    </Card>
  );
}
